import { useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { unsolicitedPolicy } from '@/data/unsolicitedPolicy';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';

export default function UnsolicitedPolicyModal({ open, onClose, onAgree }) {
  const { language } = useLanguage();
  const policy = unsolicitedPolicy[language] || unsolicitedPolicy.pt;
  const scrollRef = useRef(null);

  const handleAgree = (checked) => {
    if (checked) {
      onAgree();
    }
  };

  const scrollToSection = (id) => {
    const container = scrollRef.current;
    if (!container) return;
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    const target = viewport?.querySelector(`#unsolicited-modal-${id}`);
    if (target && viewport) {
      viewport.scrollTo({ top: target.offsetTop - viewport.offsetTop - 16, behavior: 'smooth' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        data-testid="unsolicited-policy-modal"
        className="bg-[#F9F8F6] border-[#333333]/10 max-w-2xl max-h-[90vh]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-light text-[#873D77] tracking-tight">
            {policy.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{policy.version}</DialogDescription>
          <p className="text-xs font-light text-[#333333]/50 mt-1">
            {policy.lastUpdated}
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-4" ref={scrollRef}>
          <div className="space-y-6 py-4">
            {policy.introduction && (
              <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                {policy.introduction}
              </div>
            )}

            {policy.tocTitle && (
              <div>
                <h3 className="text-base font-normal text-[#333333] mb-3">{policy.tocTitle}</h3>
                <nav className="flex flex-col gap-1.5">
                  {policy.sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollToSection(s.id)}
                      className="text-sm font-light text-[#873D77] hover:underline text-left transition-colors duration-200"
                    >
                      {s.title}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            <div className="w-full h-[1px] bg-[#333333]/10" />

            {policy.sections.map((section) => (
              <div key={section.id} id={`unsolicited-modal-${section.id}`}>
                <h3 className="text-sm font-normal text-[#333333] mb-1.5">
                  {section.title}
                </h3>
                <p className="text-xs font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="border-t border-[#333333]/10 pt-4 mt-2">
          <div className="flex items-start gap-3">
            <Checkbox
              id="policy-agree"
              data-testid="unsolicited-policy-agree-checkbox"
              onCheckedChange={handleAgree}
              className="mt-0.5 border-[#873D77]/40 data-[state=checked]:bg-[#873D77] data-[state=checked]:border-[#873D77]"
            />
            <label
              htmlFor="policy-agree"
              className="text-xs font-light text-[#333333]/90 leading-relaxed cursor-pointer"
            >
              {policy.agreeText}
            </label>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
